import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const pdfT = {
  fr: {
    monthlyReport: 'Rapport Mensuel',
    generatedOn: 'Généré le',
    totalRevenue: 'RECETTES TOTALES',
    totalOrders: 'TOTAL COMMANDES',
    avgOrderValue: 'VALEUR MOY. COMMANDE',
    bestSeller: 'MEILLEURE VENTE',
    dailyBreakdown: 'Revenus Journaliers',
    date: 'Date',
    orders: 'Commandes',
    revenue: 'Recettes (MAD)',
    productPerformance: 'Performance des Produits',
    product: 'Produit',
    unitsSold: 'Unités vendues',
    orderDetails: 'Détails des Commandes',
    dateTime: 'Date & Heure',
    items: 'Articles',
    total: 'Total',
    page: 'Page',
    of: 'sur',
    na: 'N/A',
  },
  en: {
    monthlyReport: 'Monthly Report',
    generatedOn: 'Generated on',
    totalRevenue: 'TOTAL REVENUE',
    totalOrders: 'TOTAL ORDERS',
    avgOrderValue: 'AVG ORDER VALUE',
    bestSeller: 'BEST SELLER',
    dailyBreakdown: 'Daily Revenue Breakdown',
    date: 'Date',
    orders: 'Orders',
    revenue: 'Revenue (MAD)',
    productPerformance: 'Product Performance',
    product: 'Product',
    unitsSold: 'Units Sold',
    orderDetails: 'Order Details',
    dateTime: 'Date & Time',
    items: 'Items',
    total: 'Total',
    page: 'Page',
    of: 'of',
    na: 'N/A',
  }
};

export function generateMonthlyReport(orders, items, shopName, month, year, lang = 'fr') {
  const doc = new jsPDF();
  const primaryColor = [198, 123, 58];
  const darkColor = [44, 26, 14];
  const tr = pdfT[lang] || pdfT['fr'];

  // ─── HEADER ───
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 35, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(shopName, 14, 15);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${tr.monthlyReport} — ${month} ${year}`, 14, 25);

  doc.setFontSize(10);
  doc.text(`${tr.generatedOn} ${new Date().toLocaleDateString('fr-MA', { timeZone: 'Africa/Casablanca' })}`, 14, 32);

  // ─── SUMMARY CARDS ───
  const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total), 0);
  const totalOrders = orders.length;
  const avgOrder = totalOrders ? (totalRevenue / totalOrders).toFixed(1) : 0;
  const bestProduct = getBestProduct(items);

  doc.setFillColor(253, 246, 238);
  doc.roundedRect(14, 42, 85, 25, 3, 3, 'F');
  doc.roundedRect(111, 42, 85, 25, 3, 3, 'F');
  doc.roundedRect(14, 72, 85, 25, 3, 3, 'F');
  doc.roundedRect(111, 72, 85, 25, 3, 3, 'F');

  doc.setTextColor(...darkColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(tr.totalRevenue, 19, 50);
  doc.text(tr.totalOrders, 116, 50);
  doc.text(tr.avgOrderValue, 19, 80);
  doc.text(tr.bestSeller, 116, 80);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text(`${totalRevenue.toFixed(0)} MAD`, 19, 62);
  doc.text(`${totalOrders}`, 116, 62);
  doc.text(`${avgOrder} MAD`, 19, 92);
  doc.setFontSize(12);
  doc.text(bestProduct || tr.na, 116, 92);

  // ─── DAILY BREAKDOWN ───
  doc.setTextColor(...darkColor);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(tr.dailyBreakdown, 14, 110);

  const dailyData = getDailyBreakdown(orders);

  autoTable(doc, {
    startY: 115,
    head: [[tr.date, tr.orders, tr.revenue]],
    body: dailyData.map(d => [d.date, d.orders, `${d.revenue} MAD`]),
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 9, textColor: darkColor },
    alternateRowStyles: { fillColor: [253, 246, 238] },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 40, halign: 'center' },
      2: { cellWidth: 60, halign: 'right' }
    },
    margin: { left: 14, right: 14 }
  });

  // ─── PRODUCT PERFORMANCE ───
  doc.addPage();

  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 20, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(tr.productPerformance, 14, 13);

  const productData = getProductPerformance(items);

  autoTable(doc, {
    startY: 28,
    head: [['#', tr.product, tr.unitsSold, tr.revenue]],
    body: productData.map((p, i) => [i + 1, p.name, p.qty, `${p.revenue.toFixed(0)} MAD`]),
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 9, textColor: darkColor },
    alternateRowStyles: { fillColor: [253, 246, 238] },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' },
      1: { cellWidth: 95 },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 45, halign: 'right' }
    },
    margin: { left: 14, right: 14 }
  });

  // ─── ORDER LIST ───
  const finalY = doc.lastAutoTable.finalY + 15;
  doc.setTextColor(...darkColor);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(tr.orderDetails, 14, finalY);

  const orderRows = orders.map(o => {
    const date = new Date(o.created_at).toLocaleString('fr-MA', {
      timeZone: 'Africa/Casablanca',
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    const orderItems = items
      .filter(i => i.order_id === o.id)
      .map(i => `${i.product_name} x${i.quantity}`)
      .join(', ');
    return [date, orderItems || '—', `${parseFloat(o.total).toFixed(0)} MAD`];
  });

  autoTable(doc, {
    startY: finalY + 5,
    head: [[tr.dateTime, tr.items, tr.total]],
    body: orderRows,
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
    bodyStyles: { fontSize: 8, textColor: darkColor },
    alternateRowStyles: { fillColor: [253, 246, 238] },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 105 },
      2: { cellWidth: 35, halign: 'right' }
    },
    margin: { left: 14, right: 14 }
  });

  // ─── FOOTER ───
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(...primaryColor);
    doc.rect(0, 285, 210, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${shopName} — ${month} ${year}`, 14, 292);
    doc.text(`${tr.page} ${i} ${tr.of} ${pageCount}`, 185, 292);
  }

  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = pdfUrl;
  link.download = `${shopName}-${month}-${year}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(pdfUrl);
  
  
}

function getBestProduct(items) {
  const map = {};
  items.forEach(i => {
    if (!map[i.product_name]) map[i.product_name] = 0;
    map[i.product_name] += i.quantity;
  });
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
  return sorted.length ? sorted[0][0] : null;
}

function getDailyBreakdown(orders) {
  const map = {};
  orders.forEach(o => {
    const d = new Date(new Date(o.created_at).toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    const key = d.toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', year: 'numeric' });
    if (!map[key]) map[key] = { orders: 0, revenue: 0 };
    map[key].orders += 1;
    map[key].revenue += parseFloat(o.total);
  });
  return Object.entries(map)
    .map(([date, data]) => ({ date, orders: data.orders, revenue: data.revenue.toFixed(0) }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

function getProductPerformance(items) {
  const map = {};
  items.forEach(i => {
    if (!map[i.product_name]) map[i.product_name] = { qty: 0, revenue: 0 };
    map[i.product_name].qty += i.quantity;
    map[i.product_name].revenue += i.price * i.quantity;
  });
  return Object.entries(map)
    .map(([name, data]) => ({ name, qty: data.qty, revenue: data.revenue }))
    .sort((a, b) => b.revenue - a.revenue);
}